import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BizButton } from '../../../Core/Components/Mini/BizButton';
import BizInput from '../../../Core/Components/Mini/BizInput';
import BizTextArea from '../../../Core/Components/Mini/BizTextArea';
import { BizAutoComplete } from '../../../Core/Components/Mini/BizAutoComplete';
import {
	useGetExpenseProjectOptions,
	useCreateTravelReport,
	useUpdateTravelReport,
} from '../../../Core/Hooks/useTravelReport';
import { TravelReport } from '../../../Core/Types/admin/timereport';
import { CreateTravelReportRequest, UpdateTravelReportRequest } from '../../../Core/Types/timereport';
import { formatLocalDate } from '../../../Core/Utils/dateUtils';
import './TravelExpenseForm.scss';

type TravelExpenseFormProps = {
	existingReport: TravelReport | null;
	onClose: () => void;
};

const TravelExpenseForm = ({ existingReport, onClose }: TravelExpenseFormProps) => {
	const { t } = useTranslation();
	const { create, statusState: createStatus } = useCreateTravelReport();
	const { update, statusState: updateStatus } = useUpdateTravelReport();
	const { projectOptions, isLoading: projectsLoading } = useGetExpenseProjectOptions();

	const isEditing = existingReport !== null;

	type ProjectOption = { Id: number; Name: string };

	const [form, setForm] = useState({
		travelReportTypeId: existingReport?.travelReportTypeId ?? 0,
		projectId: existingReport?.projectId ?? 0,
		activityId: existingReport?.activityId ?? 0,
		date: existingReport?.date.split('T')[0] ?? formatLocalDate(new Date()),
		dateTo: '',
		from: existingReport?.fromLocation ?? '',
		to: existingReport?.toLocation ?? '',
		distance: existingReport?.units ?? 0,
		amount: 0,
		vat: 0,
		comment: existingReport?.comment ?? '',
	});

	useEffect(() => {
		if (!isEditing && projectOptions.length === 1) {
			setForm((prev) => (prev.projectId ? prev : { ...prev, projectId: projectOptions[0].Id }));
		}
	}, [isEditing, projectOptions]);
	const hasSelectedProject = projectOptions.some((project) => project.Id === form.projectId);

	const handleProjectChange = (opt: ProjectOption | null) => {
		setForm((prev) => ({ ...prev, projectId: opt?.Id ?? 0 }));
	};

	useEffect(() => {
		if (createStatus === 'SUCCESS' || updateStatus === 'SUCCESS') {
			onClose();
		}
	}, [createStatus, updateStatus, onClose]);

	const handleSave = () => {
		if (!hasSelectedProject) return;

		if (isEditing) {
			const data: UpdateTravelReportRequest = {
				id: existingReport.id,
				...form,
			};
			update(data);
		} else {
			const data: CreateTravelReportRequest = { ...form };
			create(data);
		}
	};

	const updateField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	const saveStatus = isEditing ? updateStatus : createStatus;

	if (projectsLoading) return <p>{t('TIMEREPORT_USER.EXPENSE.FORMS.STATE.LOADING')}</p>;

	return (
		<div className='biz-travel-expense-form'>
			<div className='biz-travel-expense-form__fields'>
				<div className='biz-travel-expense-form__field'>
					<span className='biz-travel-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.SHARED.PROJECT')}</span>
					<BizAutoComplete
						options={projectOptions}
						value={projectOptions.find((p) => p.Id === form.projectId) ?? undefined}
						onChange={(opt) => handleProjectChange(opt)}
						componentLabel={t('TIMEREPORT_USER.EXPENSE.FORMS.PLACEHOLDERS.SEARCH_PROJECT')}
					/>
				</div>

				<div className='biz-travel-expense-form__row'>
					<div className='biz-travel-expense-form__field'>
						<span className='biz-travel-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.SHARED.DATE')}</span>
						<BizInput
							type='date'
							value={form.date}
							onChange={(e) => updateField('date', e.target.value)}
						/>
					</div>
					{!isEditing && (
						<div className='biz-travel-expense-form__field'>
							<span className='biz-travel-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.SHARED.DATE_TO')}</span>
							<BizInput
								type='date'
								value={form.dateTo}
								onChange={(e) => updateField('dateTo', e.target.value)}
								min={form.date}
							/>
						</div>
					)}
				</div>

				<div className='biz-travel-expense-form__row'>
					<div className='biz-travel-expense-form__field'>
						<span className='biz-travel-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.TRAVEL.FROM')}</span>
						<BizInput
							value={form.from}
							onChange={(e) => updateField('from', e.target.value)}
						/>
					</div>
					<div className='biz-travel-expense-form__field'>
						<span className='biz-travel-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.TRAVEL.TO')}</span>
						<BizInput
							value={form.to}
							onChange={(e) => updateField('to', e.target.value)}
						/>
					</div>
				</div>

				<div className='biz-travel-expense-form__field'>
					<span className='biz-travel-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.TRAVEL.DISTANCE')}</span>
					<BizInput
						type='number'
						value={String(form.distance)}
						onChange={(e) => updateField('distance', parseFloat(e.target.value) || 0)}
						min='0'
					/>
				</div>

				<div className='biz-travel-expense-form__field'>
					<span className='biz-travel-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.SHARED.COMMENT')}</span>
					<BizTextArea
						value={form.comment}
						onChange={(e) => updateField('comment', e.target.value)}
					/>
				</div>
			</div>

			<div className='biz-travel-expense-form__actions dialog-btn-actions'>
				<BizButton
					text={isEditing ? t('TIMEREPORT_USER.EXPENSE.FORMS.ACTIONS.UPDATE') : t('TIMEREPORT_USER.EXPENSE.FORMS.ACTIONS.CREATE')}
					onClick={handleSave}
					state={saveStatus}
					// icon='far fa-save'
					disabled={!hasSelectedProject}
					isLead
					legacy={false}
				/>
			</div>
		</div>
	);
};

export default TravelExpenseForm;
